import { Box, TableBody, TableRow, Tooltip, useTheme } from '@mui/material';

import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import { calculateSecondsToHoursAndMinutes } from '@libs/dateTime';

import { summarizeDays } from '../statusTokens';
import {
    timesheetSummaryHeaders,
    timesheetSummaryHeaderFullNames
} from 'timeAndAttendance/Timesheet/common';

const lateBreakdown = (summary) =>
    `On site late ${summary.lateOnsite || 0} · Remote late ${
        summary.lateRemote || 0
    }`;

/**
 * Month-by-month summary table (common table components): one row per month
 * with the classic summary columns (In Office, Remote, Late, CL, SL, ML/PL,
 * CMPL, Other, Holiday) and the worked/shift hours last, plus a totals row
 * when several months are shown. Counts come from the same per-day
 * resolution the calendars render. Scrolls horizontally on small screens.
 *
 * props:
 *   monthSheets   [{ month, title, daySheet }]
 *   monthlyTotals { [monthIndex0]: { totalDuration, totalShiftDuration } }
 */
const MonthlySummaryTable = ({ monthSheets = [], monthlyTotals = {}, sx }) => {
    const { palette } = useTheme();

    const summaryKeys = Object.keys(timesheetSummaryHeaders);
    const rows = monthSheets.map(({ month, title, daySheet }) => ({
        month,
        title,
        summary: summarizeDays(daySheet),
        totals: monthlyTotals?.[month - 1] || null
    }));

    // With a single month the totals row would just repeat it — hide it.
    const showTotalsRow = rows.length > 1;

    const totalSummary = [...summaryKeys, 'lateOnsite', 'lateRemote'].reduce(
        (acc, key) => ({
            ...acc,
            [key]: rows.reduce((sum, row) => sum + (row.summary[key] || 0), 0)
        }),
        {}
    );
    const totalDuration = rows.reduce(
        (sum, row) => sum + (row.totals?.totalDuration || 0),
        0
    );
    const totalShiftDuration = rows.reduce(
        (sum, row) => sum + (row.totals?.totalShiftDuration || 0),
        0
    );

    const hoursOf = (totals) =>
        totals
            ? `${calculateSecondsToHoursAndMinutes(
                  totals.totalDuration || 0
              )}/${calculateSecondsToHoursAndMinutes(
                  totals.totalShiftDuration || 0
              )}`
            : '-';

    const centerSx = { textAlign: 'center', whiteSpace: 'nowrap' };
    const totalRowSx = {
        fontWeight: 700,
        borderTop: `2px solid ${palette.divider}`
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'auto', ...sx }}>
            <TableContainer sx={{ boxShadow: 'none', minWidth: 760 }}>
                <TableHeaderContainer>
                    <TableCellContainer
                        typography='caption'
                        sx={{ pl: 2, fontWeight: 'bold' }}
                    >
                        Month
                    </TableCellContainer>
                    {summaryKeys.map((key) => (
                        <TableCellContainer
                            key={key}
                            typography='caption'
                            sx={{ fontWeight: 'bold', ...centerSx }}
                        >
                            <Tooltip
                                title={
                                    timesheetSummaryHeaderFullNames[key] || ''
                                }
                            >
                                <span style={{ cursor: 'help' }}>
                                    {timesheetSummaryHeaders[key]}
                                </span>
                            </Tooltip>
                        </TableCellContainer>
                    ))}
                    <TableCellContainer
                        typography='caption'
                        sx={{ fontWeight: 'bold', ...centerSx }}
                    >
                        Hours
                    </TableCellContainer>
                </TableHeaderContainer>
                <TableBody>
                    {rows.map(({ month, title, summary, totals }) => (
                        <TableRow key={month}>
                            <TableCellContainer
                                sx={{ pl: 2, fontWeight: 600 }}
                            >
                                {title}
                            </TableCellContainer>
                            {summaryKeys.map((key) => (
                                <TableCellContainer key={key} sx={centerSx}>
                                    {key === 'arrivedLate' ? (
                                        <Tooltip title={lateBreakdown(summary)}>
                                            <span style={{ cursor: 'help' }}>
                                                {summary[key] || 0}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        summary[key] || 0
                                    )}
                                </TableCellContainer>
                            ))}
                            <TableCellContainer sx={centerSx}>
                                {hoursOf(totals)}
                            </TableCellContainer>
                        </TableRow>
                    ))}
                    {showTotalsRow && (
                        <TableRow>
                            <TableCellContainer
                                sx={{ pl: 2, ...totalRowSx }}
                            >
                                Total
                            </TableCellContainer>
                            {summaryKeys.map((key) => (
                                <TableCellContainer
                                    key={key}
                                    sx={{ ...centerSx, ...totalRowSx }}
                                >
                                    {key === 'arrivedLate' ? (
                                        <Tooltip
                                            title={lateBreakdown(totalSummary)}
                                        >
                                            <span style={{ cursor: 'help' }}>
                                                {totalSummary[key] || 0}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        totalSummary[key] || 0
                                    )}
                                </TableCellContainer>
                            ))}
                            <TableCellContainer
                                sx={{ ...centerSx, ...totalRowSx }}
                            >
                                {hoursOf({ totalDuration, totalShiftDuration })}
                            </TableCellContainer>
                        </TableRow>
                    )}
                </TableBody>
            </TableContainer>
        </Box>
    );
};

export { MonthlySummaryTable as default, MonthlySummaryTable };
